// src/apis/outlookService.ts

import axios from "axios";

import axiosServices from "../utils/axios";
import { normalizeEmailBody } from "../utils/emailHelpers";

import type {
  EmailListItem,
  EmailDetails,
  Attachment,
  ConversationMessage,
  OutlookFolder,
  MailboxKey,
  ScheduledMessageType,
  ScheduledSend,
} from "../utils/types/email";
import type { EmailAttachmentPayload } from "@/components/email/utils/composerAttachments";
import type { RecipientAddress, ReplyRecipients } from "@/components/email/utils/replyFlow";

interface ApiResponse<T> {
  data: T;
}

export interface OutlookEmailPage {
  items: EmailListItem[];
  nextCursor: string | null;
}

/** Always include the mailbox param so every request is explicit about which
 *  mailbox is being accessed. */
const withMailbox = <T extends Record<string, unknown>>(
  params: T,
  mailbox: MailboxKey
): T & { mailbox: MailboxKey } => ({ ...params, mailbox });

export const fetchEmailsList = async (params: {
  folderId?: string;
  top?: number;
  filter?: string;
  cursor?: string | null;
  orderby?: string;
  mailbox: MailboxKey;
}): Promise<OutlookEmailPage> => {
  const { mailbox, ...rest } = params;
  const requestParams = withMailbox({ top: 25, ...rest }, mailbox);
  const response = await axiosServices.get<{
    data: EmailListItem[];
    nextCursor: string | null;
    success: boolean;
  }>("/outlook", { params: requestParams });
  return {
    items: response.data.data,
    nextCursor: response.data.nextCursor ?? null,
  };
};

export const fetchEmailDetailsById = async (
  id: string,
  mailbox: MailboxKey
): Promise<EmailDetails> => {
  const response = await axiosServices.get<ApiResponse<EmailDetails>>(
    `/outlook/${id}`,
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const fetchConversationMessages = async (
  conversationId: string,
  mailbox: MailboxKey,
): Promise<ConversationMessage[]> => {
  const response = await axiosServices.get<ApiResponse<ConversationMessage[]>>(
    `/outlook/conversations/${encodeURIComponent(conversationId)}`,
    { params: withMailbox({}, mailbox) },
  );
  return response.data.data;
};

export const downloadAttachment = async (
  emailId: string,
  attachmentId: string,
  mailbox: MailboxKey
): Promise<Blob> => {
  const response = await axiosServices.get(
    `/outlook/${emailId}/attachments/${attachmentId}`,
    { params: withMailbox({ format: "binary" }, mailbox), responseType: "blob" }
  );
  return response.data;
};

export const updateEmail = async (
  id: string,
  data: Partial<EmailDetails>,
  mailbox: MailboxKey
): Promise<EmailDetails> => {
  const response = await axiosServices.patch<ApiResponse<EmailDetails>>(
    `/outlook/${id}`,
    data,
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const linkAttachmentToJob = async (
  jobId: string,
  docTypeId: string,
  emailId: string,
  attachment: Attachment,
  mailbox: MailboxKey
): Promise<{ success: boolean }> => {
  try {
    const blob = await downloadAttachment(emailId, attachment.id, mailbox);
    const signedUrlResponse = await axiosServices.get("/signed_url", {
      params: {
        ContentType: attachment.contentType,
        key: attachment.name,
        operation: "put",
        jobId,
      },
    });
    const { pre_signed_url: signedUrl, final_key: fileKey } =
      signedUrlResponse.data.data;
    await axios.put(signedUrl, blob, {
      headers: { "Content-Type": attachment.contentType },
    });
    await axiosServices.post(`/jobs/${jobId}/documents`, {
      key: fileKey,
      mime_type: attachment.contentType,
      size: attachment.size,
      type_id: docTypeId,
    });
    return { success: true };
  } catch (error) {
    console.error("Failed to link attachment:", error);
    throw error;
  }
};

export const moveEmail = async (
  id: string,
  folderId: string,
  mailbox: MailboxKey
): Promise<{ success: boolean; newId?: string }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean; newId?: string }>>(
    `/outlook/${id}/move/${folderId}`,
    {},
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const deleteEmail = async (
  id: string,
  mailbox: MailboxKey
): Promise<{ success: boolean }> => {
  const response = await axiosServices.delete<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}`,
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const fetchFolders = async (mailbox: MailboxKey): Promise<OutlookFolder[]> => {
  const response = await axiosServices.get<ApiResponse<OutlookFolder[]>>(
    "/outlook/folders",
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const searchEmails = async (params: {
  q: string;
  top?: number;
  folderId?: string;
  mailbox: MailboxKey;
}): Promise<EmailListItem[]> => {
  const { mailbox, ...rest } = params;
  const response = await axiosServices.get<ApiResponse<EmailListItem[]>>(
    "/outlook/search",
    { params: withMailbox({ q: rest.q, top: rest.top ?? 25, folderId: params.folderId || undefined }, mailbox) }
  );
  return response.data.data;
};

export const sendEmail = async (
  data: {
    subject: string;
    body: { content: string; contentType: "HTML" | "Text" };
    toRecipients: { address: string; name?: string }[];
    ccRecipients?: { address: string; name?: string }[];
    bccRecipients?: { address: string; name?: string }[];
    attachments?: EmailAttachmentPayload[];
  },
  mailbox: MailboxKey
): Promise<{ success: boolean }> => {
  const normalizedBodyContent = normalizeEmailBody(data.body?.content);
  // POST /outlook: mailbox goes in the request body per the API spec
  const payload = {
    ...data,
    body: {
      ...data.body,
      content: normalizedBodyContent,
      contentType: "HTML" as const,
    },
    mailbox,
  };
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook`,
    payload
  );
  return response.data.data;
};

export const ACCOUNTING_EMAIL = import.meta.env.VITE_ACCOUNTING_EMAIL || "";

export const forwardEmail = async (
  id: string,
  toRecipients: { address: string }[],
  mailbox: MailboxKey,
  comment: string = ""
): Promise<{ success: boolean }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}/forward`,
    { toRecipients, comment },
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const replyToEmail = async (
  id: string,
  message: string,
  replyAll: boolean = false,
  attachments: EmailAttachmentPayload[] = [],
  mailbox: MailboxKey,
  recipients?: ReplyRecipients,
  subject?: string
): Promise<{ success: boolean }> => {
  const normalizedMessage = normalizeEmailBody(message);
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}/reply`,
    { message: normalizedMessage, replyAll, attachments, subject, recipients },
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const addEmailTag = async (
  messageId: string,
  value: string,
  mailbox: MailboxKey
): Promise<{ categories: string[] }> => {
  const response = await axiosServices.post<ApiResponse<{ categories: string[] }>>(
    `/outlook/${messageId}/tag`,
    { value },
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const removeEmailTag = async (
  messageId: string,
  value: string,
  mailbox: MailboxKey
): Promise<{ categories: string[] }> => {
  const response = await axiosServices.delete<ApiResponse<{ categories: string[] }>>(
    `/outlook/${messageId}/tag`,
    { params: withMailbox({ value }, mailbox) }
  );
  return response.data.data;
};

export interface BulkMoveEmailResult {
  oldId: string;
  newId?: string;
  parentFolderId?: string;
  success: boolean;
  message?: string;
  emailAssociationUpdate?: { updated: boolean; reason?: string };
}

export interface BulkMoveEmailsResponse {
  requested: number;
  moved: number;
  failed: number;
  destinationFolderId: string;
  results: BulkMoveEmailResult[];
}

export const moveEmails = async (
  messageIds: string[],
  folderId: string,
  mailbox: MailboxKey
): Promise<BulkMoveEmailsResponse> => {
  const response = await axiosServices.post<ApiResponse<BulkMoveEmailsResponse>>(
    "/outlook/bulk-move",
    { messageIds, folderId },
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export interface FavoriteFolder {
  folderId: string;
  displayName: string;
  mailboxKey: MailboxKey;
  sortOrder: number;
}

export interface CreateScheduledSendPayload {
  messageType: ScheduledMessageType;
  emailId?: string;
  replyAll?: boolean;
  recipients?: ReplyRecipients;
  subject?: string;
  message: string;
  attachments?: EmailAttachmentPayload[];
  moveToFolderId?: string | null;
  scheduledFor: string;
  timezone: string;
}

export const createScheduledSend = async (
  payload: CreateScheduledSendPayload,
  mailbox: MailboxKey
): Promise<ScheduledSend> => {
  const response = await axiosServices.post<ApiResponse<ScheduledSend>>(
    "/outlook/scheduled",
    payload,
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};

export const fetchScheduledSends = async (
  mailbox?: MailboxKey
): Promise<ScheduledSend[]> => {
  const response = await axiosServices.get<ApiResponse<ScheduledSend[]>>(
    "/outlook/scheduled",
    { params: mailbox ? { mailbox } : {} }
  );
  return response.data.data;
};

export interface UpdateScheduledSendPayload {
  recipients?: ReplyRecipients;
  subject?: string;
  message?: string;
  attachments?: EmailAttachmentPayload[];
  moveToFolderId?: string | null;
  scheduledFor?: string;
  timezone?: string;
}

export const updateScheduledSend = async (
  id: string,
  payload: UpdateScheduledSendPayload
): Promise<ScheduledSend> => {
  const response = await axiosServices.patch<ApiResponse<ScheduledSend>>(
    `/outlook/scheduled/${id}`,
    payload
  );
  return response.data.data;
};

export const cancelScheduledSend = async (
  id: string
): Promise<{ success: boolean }> => {
  const response = await axiosServices.delete<ApiResponse<{ success: boolean }>>(
    `/outlook/scheduled/${id}`
  );
  return response.data.data;
};

export const sendScheduledNow = async (
  id: string
): Promise<{ success: boolean }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook/scheduled/${id}/send-now`,
    {}
  );
  return response.data.data;
};

export const fetchFavoriteFolders = async (
  mailbox: MailboxKey
): Promise<FavoriteFolder[]> => {
  const response = await axiosServices.get<ApiResponse<FavoriteFolder[]>>(
    "/outlook/folders/favorites",
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data ?? [];
};

export const saveFavoriteFolders = async (
  mailbox: MailboxKey,
  favorites: Pick<FavoriteFolder, "folderId" | "displayName">[]
): Promise<FavoriteFolder[]> => {
  const response = await axiosServices.put<ApiResponse<FavoriteFolder[]>>(
    "/outlook/folders/favorites",
    { favorites },
    { params: withMailbox({}, mailbox) }
  );
  return response.data.data;
};
